// @givmo/assistant-panel — the seven operations, addressed by URL.
//
// ══ WHY THIS FILE EXISTS AT ALL ════════════════════════════════════════════════════════════════
//
// Neither family this panel talks to is in the committed OpenAPI snapshot: the assistant
// conversation/turn routes and the `/org/{org_ref}/assistant/consent` trio both landed after the
// last refresh, so there is no generated SDK operation, no generated type and no query-option
// factory for any of the seven. `api-client.md` § *A whole ROUTE the snapshot lacks* is the
// sanctioned disposition, and `@givmo/org-console` is the shipped precedent — `givmoClient` is the
// same fetch client the generated operations bind to, so a call made here inherits the whole
// interceptor stack (bearer token, App Check, `X-Request-Id`, the `{data, meta, links}` unwrap, and
// the `GivmoError` mapping). Nothing about the request differs from a generated one except who
// wrote the URL. These go away when a routine refresh absorbs both families.
//
// ══ THE ONE HEADER THAT DECIDES WHICH PROTOCOL YOU GET ═════════════════════════════════════════
//
// `POST …/messages` is CONTENT-NEGOTIATED. The server runs the turn INLINE and answers `200` with
// the finished message when the request asks for `application/json` and does NOT mention the event
// stream; otherwise it queues the turn and answers `202 {turn_id, status, stream_path}` for the
// client to tail over SSE. The shared client sets only `Content-Type`, so today a bare send lands on
// the streaming path by ACCIDENT (fetch's default `Accept: */*`). {@link SEND_ACCEPT} states the
// intent instead: it names the event stream, so the synchronous branch can never be taken, and it
// also names JSON because the `202` body itself is JSON. `assistant-transport.test.ts` pins it —
// without that pin, one default-header change upstream silently swaps this panel onto a protocol
// with no streaming, no stop control and no resume.
//
// ══ WHAT A LIST READ CAN HONESTLY CLAIM ════════════════════════════════════════════════════════
//
// The conversation list answers the paginated envelope and the middleware hands back the bare `data`
// array with every top-level sibling discarded — `meta` is unreachable, so nothing here may state a
// total. This panel only ever asks "is there an existing thread for this surface", which the first
// page answers.

import { givmoClient, isGivmoError, firstErrorCode, firstErrorContext } from '@givmo/api-client';

/**
 * The `Accept` for a send. Names the event stream (so the server never takes its synchronous
 * branch) and JSON (so the `202` body parses). See the header note above.
 */
export const SEND_ACCEPT = 'text/event-stream, application/json';

/**
 * How many conversations to scan when locating this surface's persistent thread. The list is newest
 * activity first, so the thread a person is actually using is at or near the top; a page of 20
 * covers a user whose consumer conversations have been busier than their portal one without paging
 * a history this panel has no other use for.
 */
export const CONVERSATION_SCAN_LIMIT = 20;

/** `/assistant/conversations` — the family every turn hangs under. */
const CONVERSATIONS = '/assistant/conversations';

/** One conversation's address. Interpolated ids are escaped: the day one stops being a UUID is not
 *  the day anybody re-reads this file. */
function conversationBase(conversationId: string): string {
  return `${CONVERSATIONS}/${encodeURIComponent(conversationId)}`;
}

/** `/org/{org_ref}` — the scope the consent instrument binds. */
function orgBase(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}`;
}

/**
 * True when this environment serves no consent instrument — never an error surface.
 *
 * ⚠ CLASSIFIED ON STATUS ALONE, and it has to be. The `/org` surface's dark answer is byte-identical
 * to a route miss by ratified design (no `error_code`, the organization reference never echoed), and
 * an organization class that binds no instrument — or one whose instrument has no effective version
 * — answers that SAME 404 deliberately, so that an unseeded environment is indistinguishable from an
 * absent route. A client reaching for an error code here would branch on a field that is never
 * present and take the wrong leg forever.
 *
 * ⚠ THIS IS THE EXPECTED STATE ON LANDING. No class has a seeded instrument yet: the copy is signed
 * separately, and a seed migration lights the whole path up with no code change on either side. The
 * panel therefore has to render the unavailable state as a first-class, calm answer rather than as
 * something that went wrong.
 *
 * ⚠ A 500 IS NOT ABSENCE. Serving a calm not-available state over a live outage would hide a real
 * problem behind a panel that looks fine.
 */
export function isConsentInstrumentUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/**
 * True when the ORGANIZATION has not accepted the instrument and a turn was refused for it.
 *
 * The send gate answers `428` with `error_code: ORG_ASSISTANT_CONSENT_REQUIRED` — the same status
 * the personal consumer gate uses, deliberately, so a client presents its sheet and retries. The
 * `error_code` is what separates them, and confusing the two is not cosmetic: the consumer refusal
 * is cured by the CALLER accepting, this one only by a representative binding the organization, so a
 * client that showed the personal sheet here would loop forever.
 */
export const ORG_CONSENT_REQUIRED_CODE = 'ORG_ASSISTANT_CONSENT_REQUIRED';

/**
 * A refused send the person can't usefully ask again right now: the send-rate window (429), the daily
 * allowance (429), the assistant paused or switched off (503, 403). Each answers its own plain words,
 * which already say when it comes back, so a host shows them as they are and offers no "Try again".
 * Anything else (a network failure, a 5xx) is transient. The text is displayed, never branched on.
 */
export function refusedSendMessage(error: unknown): string | null {
  if (!isGivmoError(error)) return null;
  if (error.status !== 429 && error.status !== 503 && error.status !== 403) return null;
  const detail = error.entries[0]?.detail;
  return typeof detail === 'string' && detail.trim().length > 0 ? detail : null;
}

export function isOrgConsentRequired(error: unknown): boolean {
  if (!isGivmoError(error) || error.status !== 428) return false;
  return firstErrorCode(error) === ORG_CONSENT_REQUIRED_CODE;
}

/**
 * The consent state carried ON the 428 itself, when the refusal had an instrument to describe.
 *
 * The gate rides the SAME shape the consent read returns, so the acceptance card renders straight
 * off the refusal with no second round-trip. It is ABSENT (not null) when the environment serves no
 * instrument — the unavailable state, where there is no sheet to show.
 */
export function orgConsentFromError(error: unknown): unknown {
  if (!isOrgConsentRequired(error) || !isGivmoError(error)) return undefined;
  const context = firstErrorContext(error);
  if (context === null || !('org_consent' in context)) return undefined;
  return context.org_consent;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/** The caller's conversations, newest activity first. Always available — a person may read their
 *  own history even when the assistant is switched off. */
export async function fetchConversations(options: { signal?: AbortSignal } = {}): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: CONVERSATIONS,
    query: { 'page[limit]': CONVERSATION_SCAN_LIMIT, 'page[offset]': 0 },
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** One conversation with its messages in render-ready order. Cross-user/missing → 404. */
export async function fetchConversation(
  conversationId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: conversationBase(conversationId),
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** The organization's consent instrument plus whether it has accepted. Any admin may read it — the
 *  per-admin first-use notice needs the body, and seeing whether the assistant may act on your own
 *  organization's data is not privileged. Binding the organization is the write below. */
export async function fetchOrgConsent(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/assistant/consent`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Create the conversation. `entryContext` is stamped ONCE, here, and every later turn in this
 * conversation is judged under it — see `use-assistant-thread.ts`.
 *
 * Answers `201` with the conversation AND its server-generated opening message.
 */
export async function createConversation(entryContext: Record<string, string>): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: CONVERSATIONS,
    body: { entry_context: entryContext },
    throwOnError: true,
  });
  return data;
}

/**
 * Queue a turn. Answers `202 {turn_id, status, stream_path}`; the caller opens `stream_path`.
 *
 * `clientMsgId` is the idempotency key and a REPLAY IS NOT A RETRY: the server returns the existing
 * turn and dispatches no second generation, so re-sending a failed turn's id would hand back the
 * dead turn forever. A person asking again is a NEW id.
 */
export async function sendMessage(
  conversationId: string,
  body: { text: string; client_msg_id: string; client_context?: Record<string, unknown> },
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${conversationBase(conversationId)}/messages`,
    headers: { Accept: SEND_ACCEPT },
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Ask the in-flight turn to stop. Conversation-level, because one active turn per conversation is
 * database-enforced, so the target is unambiguous and the client need not track the turn id.
 *
 * The worker stops at its next round boundary and the stream ends with a terminal `turn_cancelled`,
 * which is what actually settles the UI — this call only requests it. Idempotent; a `409` means
 * there was nothing in flight, which is a race the caller can ignore rather than an error.
 */
export async function cancelTurn(conversationId: string): Promise<void> {
  await givmoClient.post({
    url: `${conversationBase(conversationId)}/cancel`,
    throwOnError: true,
  });
}

/**
 * Bind the organization to the instrument, via an authorized representative.
 *
 * The body carries ONLY the hash of the copy this client DISPLAYED. A stale hash is refused (409)
 * rather than recorded, so a sheet showing outdated text can never bind an organization — which is
 * also why the card renders the server's own instrument body rather than a copy compiled into this
 * package. Idempotent at the current version.
 */
export async function acceptOrgConsent(orgRef: string, contentHash: string): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${orgBase(orgRef)}/assistant/consent`,
    body: { content_hash: contentHash },
    throwOnError: true,
  });
  return data;
}
